import React from 'react'
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  alpha
} from '@mui/material'
import {
  CloudUpload as UploadIcon
} from '@mui/icons-material'
import { Document } from '../../../types/api'
import DocumentListGrouped from '../../documents/DocumentListGrouped'

interface PatientDocumentsSectionProps {
  documents: Document[]
  loading?: boolean
  onUpload: () => void
  onViewDocument: (doc: Document) => void
  onDocumentVerified?: () => void
  /** Modul compact - fără header propriu (folosit când e în CollapsibleSection) */
  compact?: boolean
}

/**
 * PatientDocumentsSection - Secțiunea de documente a pacientului
 * 
 * Design:
 * - Modul normal: Header cu titlu și buton upload
 * - Modul compact: Doar lista de documente (pentru CollapsibleSection)
 * - Lista de documente grupate pe tip
 * - Empty state frumos
 */
const PatientDocumentsSection: React.FC<PatientDocumentsSectionProps> = ({
  documents,
  loading,
  onUpload,
  onViewDocument,
  onDocumentVerified,
  compact = false
}) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const { designSystem } = theme
  const colors = isDark ? designSystem.colors.dark : designSystem.colors.light

  // Content comun
  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )
    }

    if (documents.length === 0) {
      return (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 4,
            px: 2,
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              color: colors.text.secondary, 
              mb: 2 
            }}
          >
            Nu există documente încărcate pentru acest pacient.
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<UploadIcon />}
            onClick={onUpload}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              height: 28,
              fontSize: '0.75rem',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
              }
            }}
          >
            Încarcă primul document
          </Button>
        </Box>
      )
    }

    return (
      <DocumentListGrouped
        documents={documents}
        onView={onViewDocument}
        onDocumentVerified={onDocumentVerified}
      />
    )
  }

  // Modul compact - doar conținutul, fără wrapper
  if (compact) {
    return <Box>{renderContent()}</Box>
  }

  // Modul normal cu header propriu (pentru compatibilitate cu vechiul layout)
  return (
    <Box
      sx={{
        backgroundColor: colors.background.paper,
        border: `1px solid ${colors.border.default}`,
        borderRadius: `${designSystem.components.card.borderRadius}px`,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${colors.border.default}`,
          backgroundColor: colors.background.elevated,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              fontSize: '1rem',
            }}
          >
            Documente Pacient
          </Typography>
          <Box
            sx={{
              backgroundColor: alpha(designSystem.colors.status.info, 0.15),
              color: designSystem.colors.status.info,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          >
            {documents.length}
          </Box>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<UploadIcon />}
          onClick={onUpload}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.8rem',
            px: 2,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: designSystem.shadows.sm,
            }
          }}
        >
          Încarcă Document
        </Button>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2 }}>
        {renderContent()}
      </Box>
    </Box>
  )
}

export default PatientDocumentsSection
